import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { occyLs } from '../../utils/localStorage';
import {
  extractPermissionKeys,
  hasPermission,
  permissionGateShouldEnforce,
  resolveRecruitmentRequestIdFromJobListingItem,
  shouldBypassCreateJobPermissionForRecruitmentRequest,
} from '../core';
import { PERMISSION_DENIED_MESSAGE, PERMISSIONS } from '../constants/permissions';
import { maybeOpenPermissionDenied } from '../actions/permissionDenied';

export function usePermissions() {
  const reduxAccount = useSelector((s) => s.auth.account);
  const account = useMemo(
    () => reduxAccount || occyLs.getObject('account'),
    [reduxAccount],
  );
  const permissionKeys = useMemo(() => extractPermissionKeys(account), [account]);
  const permissionSet = useMemo(() => new Set(permissionKeys), [permissionKeys]);
  const enforcing = useMemo(() => permissionGateShouldEnforce(account), [account]);
  const hasWildcard = useMemo(() => permissionSet.has('*'), [permissionSet]);
  const can = useMemo(
    () => (key) =>
      !key || !enforcing || hasWildcard || hasPermission(permissionSet, key),
    [enforcing, permissionSet, hasWildcard],
  );

  return { permissionKeys, permissionSet, can, hasWildcard, enforcing };
}

/** @param {string} permissionKey */
export function useCan(permissionKey) {
  return usePermissions().can(permissionKey);
}

export function usePermissionActionGuard() {
  const dispatch = useDispatch();
  const store = useStore();
  const { can } = usePermissions();

  const runIfAllowed = useCallback(
    (requiredPermission, onAllowed, options = {}) => {
      const permissionKey =
        typeof requiredPermission === 'string'
          ? requiredPermission.trim()
          : requiredPermission;
      if (can(permissionKey)) {
        if (typeof onAllowed === 'function') onAllowed();
        return true;
      }

      maybeOpenPermissionDenied(
        dispatch,
        {
          requiredPermission: permissionKey || requiredPermission,
          message: options?.message || PERMISSION_DENIED_MESSAGE,
        },
        () => store.getState(),
      );
      return false;
    },
    [can, dispatch, store],
  );

  return { runIfAllowed, can };
}

export function guardDuplicateTemplatePicker(
  runIfAllowed,
  { setListing, listing, setduplicateTemplateModal },
) {
  runIfAllowed(PERMISSIONS.CREATE_TEMPLATE, () => {
    setListing({ ...listing, perPage: 50, query: '' });
    setduplicateTemplateModal(true);
  });
}

export function guardEditJobAction(runIfAllowed, onAllowed, job) {
  const recruitmentRequestId = resolveRecruitmentRequestIdFromJobListingItem(job);
  if (
    shouldBypassCreateJobPermissionForRecruitmentRequest(
      recruitmentRequestId,
      PERMISSIONS.CREATE_UPDATE_JOB,
    )
  ) {
    if (typeof onAllowed === 'function') onAllowed();
    return true;
  }
  return runIfAllowed(PERMISSIONS.CREATE_UPDATE_JOB, onAllowed);
}

export function guardDuplicateJobAction(runIfAllowed, job, duplicateJob) {
  runIfAllowed(PERMISSIONS.CREATE_UPDATE_JOB, () => duplicateJob(job));
}

export function guardExtendJobExpiryAction(_runIfAllowed, job, openExtendModal) {
  openExtendModal(job);
}
